import styled, { css } from 'styled-components';

import { RouterLink } from 'components/Link';
import { bodyMicroText, colors, shadows, transition } from 'theme/theme';

/*
 * By using CSS variables we remove the need for JS to communicate between components
 * through transient properties, relying only on CSS.
 */
const cartPillItemBackgroundColor = '--background-color';
const cartPillItemLinkColorVar = '--link-color';
const cartPillBorderChevronMultiplierVar = '--chevron-multiplier';
const cartPillBorderRadiusMultiplierVar = '--radius-multiplier';
const cartPillBorderVar = '--border-color';
const cartPillUlWidthVar = '--width';

const pillCss = css`
  border-radius: 6px 6px
    calc(var(${cartPillBorderRadiusMultiplierVar}, 1) * 6px)
    calc(var(${cartPillBorderRadiusMultiplierVar}, 1) * 6px);
  color: ${colors.white};
  transition: ${transition};
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding-left: 6px;
  padding-right: 6px;
  font-size: 13px;
  user-select: none;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const selectablePillCss = css`
  color: ${colors.white};
  background: ${colors.brandBlue};
  outline-color: ${colors.hoverBlue};

  &:hover {
    color: ${colors.white};
    background: ${colors.hoverBlue};
  }
`;

const emptyPillCss = css`
  color: ${colors.darkContentGrey};

  &:hover {
    background: ${colors.selectionGrey};
  }
`;

export const CartPillDetails = styled.details`
  position: relative;
  display: inline-flex;

  ${cartPillBorderChevronMultiplierVar}: 1;
  ${cartPillBorderRadiusMultiplierVar}: 1;

  &[open] {
    ${cartPillBorderChevronMultiplierVar}: -1;
    ${cartPillBorderRadiusMultiplierVar}: 0;
  }

  &:not([data-empty]) {
    summary {
      ${selectablePillCss};
    }

    ${cartPillBorderVar}: 1px solid ${colors.calloutGrey};
    ${cartPillUlWidthVar}: 147px;
  }

  &[data-empty] {
    summary {
      ${emptyPillCss}
    }

    ${cartPillBorderVar}: none;
    ${cartPillUlWidthVar}: 180px;
  }
`;

export const CartPillSummary = styled.summary`
  &::marker {
    content: '';
  }

  &::-webkit-details-marker /* Safari */ {
    display: none;
  }

  min-width: 51px;
  ${pillCss};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const CartPillCountAndIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const CartPillListItemEmptyMessage = styled.div`
  color: ${colors.lightContentGrey};
  padding: 5px 10px;
  ${bodyMicroText};
  border-bottom: 1px solid ${colors.calloutGrey};
`;

export const CartPillSummaryChevronContainer = styled.div`
  transition: ${transition};
  transform: scaleY(var(${cartPillBorderChevronMultiplierVar}, 1));
`;

export const CartPillLink = styled(RouterLink)`
  ${pillCss};
  ${selectablePillCss};
`;

export const CartPillCount = styled.span`
  width: 12px;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
`;

export const CartPillListUl = styled.ul`
  position: absolute;
  right: 0;
  top: 100%;
  width: var(${cartPillUlWidthVar});
  border: 1px solid ${colors.calloutGrey};
  box-shadow: ${shadows.toolbar};
  background: ${colors.white};
  border-radius: 8px 0 8px 8px;
  overflow: hidden;
  margin: 0;
  padding: 0;
  z-index: 2;
`;

export const CartPillListItemLi = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;

  &:not(:first-child) {
    border-top: ${cartPillBorderVar};
  }

  &[data-selected='true'] {
    ${cartPillItemBackgroundColor}: ${colors.softBlue};
    ${cartPillItemLinkColorVar}: ${colors.skyBlue};
  }
`;

export const CartPillListItemLink = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
  color: ${colors.lightContentGrey};
  transition: ${transition};
  font-size: 11px;
  user-select: none;

  background: var(${cartPillItemBackgroundColor}, transparent);
  ${cartPillItemLinkColorVar}: ${colors.brandBlue};

  &:hover {
    color: ${colors.lightContentGrey};
    text-decoration: none;
    background: ${colors.calloutGrey};
  }
`;

export const CartPillListItemCount = styled.span`
  background: var(${cartPillItemLinkColorVar});
  transition: ${transition};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 11px;
`;

export const CartPillListItemOrgName = styled.span``;
